/**
 * ReviewModal Component
 *
 * Modal for reviewing posts with markdown content preview.
 * Redesigned for better mobile usability and visual appeal.
 * Includes action buttons for Approve, Decline, and Request Edit.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import ReactMarkdown from "react-markdown";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Content Hub components
import SocialCopyPreview from "./SocialCopyPreview";

function ReviewModal({ open, post, onClose, onApprove, onReject, onEdit, loading, loadingStage }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  if (!post) return null;

  const getLoadingMessage = () => {
    switch (loadingStage) {
      case "starting":
        return "Starting workflow...";
      case "updating-status":
        return "Updating post status...";
      case "triggering-workflow":
        return "Triggering automation...";
      case "processing":
        return "Processing request...";
      case "linkedin":
        return "Posting to LinkedIn...";
      case "x":
        return "Posting to X...";
      case "threads":
        return "Posting to Threads...";
      case "complete":
        return "Complete!";
      default:
        return "Processing...";
    }
  };

  // Get status color and icon
  const getStatusConfig = (status) => {
    switch (status) {
      case "Posted":
        return { color: "#10B981", bg: "#D1FAE5", icon: "check_circle", label: "Published" };
      case "Pending Approval":
        return { color: "#F59E0B", bg: "#FEF3C7", icon: "pending", label: "Pending Review" };
      case "Rejected":
        return { color: "#EF4444", bg: "#FEE2E2", icon: "cancel", label: "Rejected" };
      default:
        return { color: "#6B7280", bg: "#F3F4F6", icon: "info", label: status };
    }
  };

  const statusConfig = getStatusConfig(post.status);

  // Format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get domain from URL
  const getDomain = (url) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return url;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          overflow: "hidden",
          maxHeight: isMobile ? "100%" : "90vh",
        },
      }}
    >
      {/* Gradient Header */}
      <MDBox
        sx={{
          background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
          pt: isMobile ? "calc(env(safe-area-inset-top) + 16px)" : 3,
          pb: 3,
          px: { xs: 2, sm: 3 },
          position: "relative",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            position: "absolute",
            top: isMobile ? "calc(env(safe-area-inset-top) + 8px)" : 8,
            right: 8,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            minWidth: "44px",
            minHeight: "44px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.25)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Status Badge */}
        <Chip
          icon={
            <Icon sx={{ color: `${statusConfig.color} !important`, fontSize: "1rem !important" }}>
              {statusConfig.icon}
            </Icon>
          }
          label={statusConfig.label}
          size="small"
          sx={{
            backgroundColor: "rgba(255,255,255,0.9)",
            color: statusConfig.color,
            fontWeight: 600,
            fontSize: "0.7rem",
            mb: 2,
            height: "28px",
            "& .MuiChip-icon": {
              marginLeft: "8px",
            },
          }}
        />

        {/* Title */}
        <MDTypography
          variant="h4"
          color="white"
          fontWeight="bold"
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            lineHeight: 1.3,
            pr: 5,
          }}
        >
          {post.title || "Untitled Post"}
        </MDTypography>

        {/* Date & Meta */}
        <MDBox display="flex" alignItems="center" mt={2} gap={2} flexWrap="wrap">
          {post.date && (
            <MDBox display="flex" alignItems="center" gap={0.5}>
              <Icon sx={{ color: "rgba(255,255,255,0.7)", fontSize: "1rem" }}>schedule</Icon>
              <MDTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 500 }}
              >
                {formatDate(post.date)}
              </MDTypography>
            </MDBox>
          )}
          {post.sourceChannel && (
            <MDBox display="flex" alignItems="center" gap={0.5}>
              <Icon sx={{ color: "rgba(255,255,255,0.7)", fontSize: "1rem" }}>source</Icon>
              <MDTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 500 }}
              >
                {post.sourceChannel}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      {/* Tab Navigation */}
      <MDBox sx={{ borderBottom: "1px solid #E5E7EB", backgroundColor: "#FAFAFA" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: "48px",
            "& .MuiTab-root": {
              minHeight: "48px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "#6B7280",
              "&.Mui-selected": {
                color: "#059669",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#059669",
              height: "3px",
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            icon={<Icon sx={{ fontSize: "1.1rem !important" }}>article</Icon>}
            iconPosition="start"
            label="Content"
          />
          <Tab
            icon={<Icon sx={{ fontSize: "1.1rem !important" }}>chat</Icon>}
            iconPosition="start"
            label="Social Copies"
          />
          <Tab
            icon={<Icon sx={{ fontSize: "1.1rem !important" }}>info</Icon>}
            iconPosition="start"
            label="Details"
          />
        </Tabs>
      </MDBox>

      <DialogContent
        sx={{
          p: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          flex: 1,
        }}
      >
        {/* Content Tab */}
        {activeTab === 0 && (
          <MDBox sx={{ p: { xs: 2.5, sm: 3 } }}>
            {/* Banner Image */}
            {post.banner_image && (
              <MDBox
                component="img"
                src={post.banner_image}
                alt={post.title}
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  mb: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              />
            )}

            {/* Abstract */}
            {post.abstract && (
              <MDBox
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: "12px",
                  backgroundColor: "#F0FDF4",
                  borderLeft: "4px solid #10B981",
                }}
              >
                <MDTypography
                  variant="body1"
                  sx={{
                    color: "#065F46",
                    fontStyle: "italic",
                    lineHeight: 1.7,
                    fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  }}
                >
                  {post.abstract}
                </MDTypography>
              </MDBox>
            )}

            {/* Article Content */}
            <MDBox
              sx={{
                "& h1": {
                  fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  fontWeight: 700,
                  color: "#111827",
                  mt: 3,
                  mb: 2,
                },
                "& h2": {
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  fontWeight: 700,
                  color: "#1F2937",
                  mt: 3,
                  mb: 2,
                },
                "& h3": {
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  fontWeight: 600,
                  color: "#374151",
                  mt: 2.5,
                  mb: 1.5,
                },
                "& p": {
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  lineHeight: 1.8,
                  color: "#4B5563",
                  mb: 2,
                },
                "& ul, & ol": {
                  mb: 2,
                  pl: 2.5,
                  "& li": {
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    lineHeight: 1.7,
                    color: "#4B5563",
                    mb: 0.5,
                  },
                },
                "& blockquote": {
                  borderLeft: "4px solid #D1D5DB",
                  pl: 2,
                  py: 1,
                  my: 2,
                  backgroundColor: "#F9FAFB",
                  borderRadius: "0 8px 8px 0",
                  "& p": {
                    color: "#6B7280",
                    fontStyle: "italic",
                    mb: 0,
                  },
                },
                "& code": {
                  backgroundColor: "#F3F4F6",
                  px: 1,
                  py: 0.25,
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  fontFamily: "monospace",
                },
                "& pre": {
                  backgroundColor: "#1F2937",
                  color: "#F9FAFB",
                  p: 2,
                  borderRadius: "8px",
                  overflow: "auto",
                  fontSize: "0.875rem",
                  "& code": {
                    backgroundColor: "transparent",
                    p: 0,
                    color: "inherit",
                  },
                },
                "& a": {
                  color: "#059669",
                  textDecoration: "underline",
                  "&:hover": {
                    color: "#047857",
                  },
                },
                "& img": {
                  maxWidth: "100%",
                  borderRadius: "8px",
                  my: 2,
                },
              }}
            >
              <ReactMarkdown>{post.content || "No content available"}</ReactMarkdown>
            </MDBox>
          </MDBox>
        )}

        {/* Social Copies Tab */}
        {activeTab === 1 && (
          <MDBox>
            <SocialCopyPreview
              linkedinCopy={post.linkedinCopy}
              xCopy={post.xCopy}
              threadsCopy={post.threadsCopy}
              instagramCopy={post.instagramCopy}
              onEdit={(platform) => {
                // Open edit modal focused on social copies
                onEdit(post);
              }}
            />
          </MDBox>
        )}

        {/* Details Tab */}
        {activeTab === 2 && (
          <MDBox sx={{ p: { xs: 2.5, sm: 3 } }}>
            {/* Source Article Link */}
            {post.article_link && (
              <MDBox sx={{ mb: 3 }}>
                <MDTypography
                  variant="overline"
                  sx={{ color: "#6B7280", fontWeight: 600, letterSpacing: "0.5px" }}
                >
                  Source Article
                </MDTypography>
                <MDBox
                  onClick={() => window.open(post.article_link, "_blank")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    mt: 1,
                    borderRadius: "12px",
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#F1F5F9",
                      borderColor: "#CBD5E1",
                    },
                    "&:active": {
                      transform: "scale(0.99)",
                    },
                  }}
                >
                  <MDBox
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: "#E0E7FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ color: "#4F46E5" }}>article</Icon>
                  </MDBox>
                  <MDBox sx={{ flex: 1, minWidth: 0 }}>
                    <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#1E293B" }}>
                      View Original Article
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      sx={{
                        color: "#64748B",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {getDomain(post.article_link)}
                    </MDTypography>
                  </MDBox>
                  <Icon sx={{ color: "#94A3B8", ml: 1 }}>open_in_new</Icon>
                </MDBox>
              </MDBox>
            )}

            {/* Post Metadata */}
            <MDBox sx={{ mb: 3 }}>
              <MDTypography
                variant="overline"
                sx={{ color: "#6B7280", fontWeight: 600, letterSpacing: "0.5px" }}
              >
                Post Information
              </MDTypography>
              <MDBox
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#6B7280" }}>
                    Status
                  </MDTypography>
                  <Chip
                    size="small"
                    label={statusConfig.label}
                    sx={{
                      backgroundColor: statusConfig.bg,
                      color: statusConfig.color,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />
                </MDBox>
                {post.date && (
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <MDTypography variant="body2" sx={{ color: "#6B7280" }}>
                      Created
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#1F2937" }}>
                      {formatDate(post.date)}
                    </MDTypography>
                  </MDBox>
                )}
                {post.sourceChannel && (
                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="body2" sx={{ color: "#6B7280" }}>
                      Source
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#1F2937" }}>
                      {post.sourceChannel}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>

            {/* Published Links - Show only for Posted status */}
            {post.status === "Posted" && (
              <MDBox sx={{ mb: 3 }}>
                <MDTypography
                  variant="overline"
                  sx={{ color: "#6B7280", fontWeight: 600, letterSpacing: "0.5px" }}
                >
                  Published Links
                </MDTypography>
                <MDBox sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {post.blog_url && (
                    <MDBox
                      onClick={() => window.open(post.blog_url, "_blank")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor: "#F0FDF4",
                        border: "1px solid #BBF7D0",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { backgroundColor: "#DCFCE7" },
                      }}
                    >
                      <Icon sx={{ color: "#16A34A", mr: 2 }}>language</Icon>
                      <MDTypography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ color: "#166534", flex: 1 }}
                      >
                        View on Blog
                      </MDTypography>
                      <Icon sx={{ color: "#16A34A", fontSize: "1rem" }}>open_in_new</Icon>
                    </MDBox>
                  )}
                  {post.linkedin_post_url && (
                    <MDBox
                      onClick={() => window.open(post.linkedin_post_url, "_blank")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor: "#EFF6FF",
                        border: "1px solid #BFDBFE",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { backgroundColor: "#DBEAFE" },
                      }}
                    >
                      <Icon sx={{ color: "#2563EB", mr: 2 }}>work</Icon>
                      <MDTypography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ color: "#1E40AF", flex: 1 }}
                      >
                        View on LinkedIn
                      </MDTypography>
                      <Icon sx={{ color: "#2563EB", fontSize: "1rem" }}>open_in_new</Icon>
                    </MDBox>
                  )}
                  {post.x_post_url && (
                    <MDBox
                      onClick={() => window.open(post.x_post_url, "_blank")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { backgroundColor: "#F1F5F9" },
                      }}
                    >
                      <MDTypography
                        variant="body1"
                        fontWeight="bold"
                        sx={{ color: "#0F172A", mr: 2 }}
                      >
                        𝕏
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ color: "#1E293B", flex: 1 }}
                      >
                        View on X
                      </MDTypography>
                      <Icon sx={{ color: "#64748B", fontSize: "1rem" }}>open_in_new</Icon>
                    </MDBox>
                  )}
                  {post.threads_post_url && (
                    <MDBox
                      onClick={() => window.open(post.threads_post_url, "_blank")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor: "#FDF4FF",
                        border: "1px solid #F5D0FE",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": { backgroundColor: "#FAE8FF" },
                      }}
                    >
                      <Icon sx={{ color: "#A855F7", mr: 2 }}>forum</Icon>
                      <MDTypography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ color: "#7E22CE", flex: 1 }}
                      >
                        View on Threads
                      </MDTypography>
                      <Icon sx={{ color: "#A855F7", fontSize: "1rem" }}>open_in_new</Icon>
                    </MDBox>
                  )}
                  {!post.blog_url &&
                    !post.linkedin_post_url &&
                    !post.x_post_url &&
                    !post.threads_post_url && (
                      <MDBox
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          backgroundColor: "#F9FAFB",
                          textAlign: "center",
                        }}
                      >
                        <MDTypography variant="body2" sx={{ color: "#9CA3AF" }}>
                          No published links available yet
                        </MDTypography>
                      </MDBox>
                    )}
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        )}
      </DialogContent>

      {/* Sticky Action Bar */}
      <MDBox
        sx={{
          p: { xs: 2, sm: 2.5 },
          pb: isMobile ? "calc(env(safe-area-inset-bottom) + 16px)" : 2.5,
          borderTop: "1px solid #E5E7EB",
          backgroundColor: "white",
        }}
      >
        {loading ? (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={2} py={1}>
            <MDBox sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress size={28} sx={{ color: "#059669" }} />
            </MDBox>
            <MDBox>
              <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#1F2937" }}>
                {getLoadingMessage()}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#6B7280" }}>
                Please wait, this may take a moment...
              </MDTypography>
            </MDBox>
          </MDBox>
        ) : (
          <MDBox display="flex" gap={1.5} flexDirection={{ xs: "column", sm: "row" }}>
            {/* Reject Button */}
            <MDButton
              onClick={() => onReject(post.id)}
              disabled={loading}
              sx={{
                flex: { xs: "auto", sm: "0 0 auto" },
                minHeight: "48px",
                px: 3,
                borderRadius: "12px",
                backgroundColor: "#FEE2E2",
                color: "#DC2626",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                order: { xs: 2, sm: 0 },
                "&:hover": {
                  backgroundColor: "#FECACA",
                  boxShadow: "none",
                },
              }}
            >
              <Icon sx={{ mr: 0.5, fontSize: "1.1rem" }}>close</Icon>
              Reject
            </MDButton>

            {/* Edit Button */}
            <MDButton
              onClick={() => onEdit(post)}
              disabled={loading}
              sx={{
                flex: { xs: "auto", sm: "0 0 auto" },
                minHeight: "48px",
                px: 3,
                borderRadius: "12px",
                backgroundColor: "#F3F4F6",
                color: "#374151",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                order: { xs: 1, sm: 1 },
                "&:hover": {
                  backgroundColor: "#E5E7EB",
                  boxShadow: "none",
                },
              }}
            >
              <Icon sx={{ mr: 0.5, fontSize: "1.1rem" }}>edit</Icon>
              Edit
            </MDButton>

            {/* Approve Button */}
            <MDButton
              onClick={() => onApprove(post)}
              disabled={loading}
              sx={{
                flex: 1,
                minHeight: "48px",
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.95rem",
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                color: "#FFFFFF !important",
                boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)",
                order: { xs: 0, sm: 2 },
                "& .MuiIcon-root": {
                  color: "#FFFFFF !important",
                },
                "&:hover": {
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  boxShadow: "0 6px 20px rgba(16, 185, 129, 0.5)",
                },
              }}
            >
              <Icon sx={{ mr: 0.5, fontSize: "1.1rem", color: "#FFFFFF !important" }}>
                check_circle
              </Icon>
              Approve & Post
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    </Dialog>
  );
}

ReviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  post: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingStage: PropTypes.string,
};

ReviewModal.defaultProps = {
  post: null,
  loading: false,
  loadingStage: "idle",
};

export default ReviewModal;
